import * as React from 'react';
import type { fetchCompleteSignupModel } from 'state/actions/complete-signup';
import { withErrorPage } from '@atlassian/help-center-common-component/error-page';
import { history } from '@atlassian/help-center-common-util/history';
import { assign, isValidLocation } from '@atlassian/help-center-common-util/location';
import Redirect, { PushRedirectWithState } from '@atlassian/help-center-common-util/redirect';

import {
    convertAbsoluteUrlToRelativeUrl,
    getLoginUrl,
    getLoginUrlForPortalId,
    getPortalUrl,
} from '@atlassian/help-center-common-util/url';
import { CompleteSignupExpiredTokenScreen } from './complete-signup-expired-token-screen';
import { CompleteSignupStateless } from './complete-signup-stateless';
import { retrieveUrlParams } from './complete-signup-url-param-retriever';

interface CompleteSignupProps {
    isFailedLoginAttempt: boolean;
    email: string;
    validationOutcome: string;
    portalId?: number;
    redirectUrl?: string;
    isLoading: boolean;
    isLoggedIn: boolean;
    newSignupLinkSuccessfullySent: boolean;
    locationSearch: string;
    fetchCompleteSignupModelAction: typeof fetchCompleteSignupModel;
    helpCenterName: string;
}

// Defined in backend in com.atlassian.servicedesk.internal.user.validation.EmailValidationResult
const KNOWN_VALIDATION_OUTCOMES = [
    'SUCCEEDED',
    'TOKEN_INVALID',
    'TOKEN_EXPIRED',
    'PROCESSING_ERROR',
    'ALREADY_SIGNED_UP',
];

const checkEmailUrl = '/user/check-email';

export class CompleteSignup extends React.Component<CompleteSignupProps> {
    componentDidMount() {
        const { fetchCompleteSignupModelAction, isLoading, locationSearch } = this.props;
        if (isLoading) {
            // Suppressing existing violation. Please fix this.
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            const { token: tokenFromUrlParams } = retrieveUrlParams(locationSearch);
            // Suppressing existing violation. Please fix this.
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            fetchCompleteSignupModelAction({ signupToken: tokenFromUrlParams });
        }
    }

    userAlreadySignedUp = () => {
        return this.props.validationOutcome === 'ALREADY_SIGNED_UP';
    };

    tokenIsInvalid = () => {
        const { validationOutcome } = this.props;
        return (
            validationOutcome === 'TOKEN_INVALID' ||
            validationOutcome === 'PROCESSING_ERROR' ||
            validationOutcome === 'SIGNUP_BLOCKED_SSO_ENFORCED' ||
            !KNOWN_VALIDATION_OUTCOMES.includes(validationOutcome)
        );
    };

    tokenIsExpired = () => {
        return this.props.validationOutcome === 'TOKEN_EXPIRED';
    };

    redirectIfNecessary = () => {
        const { isFailedLoginAttempt, email, portalId, redirectUrl, isLoggedIn, newSignupLinkSuccessfullySent } =
            this.props;
        if (isFailedLoginAttempt) {
            return <Redirect to={getLoginUrlForPortalId(portalId)} />;
        }
        if (isLoggedIn) {
            if (redirectUrl) {
                if (isValidLocation(redirectUrl)) {
                    assign(convertAbsoluteUrlToRelativeUrl(redirectUrl));
                    return null;
                }
            }

            const defaultRedirectUrl = getPortalUrl(portalId);
            return <Redirect to={defaultRedirectUrl} />;
        }
        if (this.userAlreadySignedUp()) {
            return <Redirect to={getLoginUrl({ redirectUrl: getPortalUrl() })} />;
        }
        if (newSignupLinkSuccessfullySent) {
            return (
                <PushRedirectWithState
                    to={checkEmailUrl}
                    state={{
                        email,
                        portalId,
                    }}
                />
            );
        }
        return null;
    };

    showTokenErrorScreenIfNecessary = () => {
        const { email, isLoading, portalId } = this.props;
        if (isLoading) {
            return null;
        }
        if (this.tokenIsInvalid()) {
            history.push(getLoginUrlForPortalId(portalId));
            return null;
        }
        if (this.tokenIsExpired()) {
            return <CompleteSignupExpiredTokenScreen email={email} portalId={portalId} />;
        }
        return null;
    };

    render() {
        const { isLoading, email, helpCenterName } = this.props;
        return (
            this.redirectIfNecessary() ||
            this.showTokenErrorScreenIfNecessary() || (
                <CompleteSignupStateless isLoading={isLoading} email={email} helpCenterName={helpCenterName} />
            )
        );
    }
}

export default withErrorPage(CompleteSignup);
